// Theme service — handles light/dark mode preference.
//
// Storage strategy:
//   1. If the user has set an explicit choice, store it under
//      'rex_mortis_theme' = 'light' | 'dark' and honour that.
//   2. Otherwise, follow the OS preference via prefers-color-scheme.
//
// Apply by setting data-theme="dark" on the <html> element. tokens.css
// has a :root[data-theme="dark"] block that overrides every color.

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'rex_mortis_theme';

function osPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Read the user's explicit choice, or null if they haven't picked. */
export function readStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    /* ignore */
  }
  return null;
}

/** The theme that should be live right now. */
export function effectiveTheme(): Theme {
  return readStoredTheme() ?? (osPrefersDark() ? 'dark' : 'light');
}

/** Apply a theme to the document root + notify any subscribers. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }
  // Also set color-scheme so native form controls (scrollbars, date pickers)
  // adapt without us styling them by hand.
  root.style.colorScheme = theme;
  // Tell anyone listening (header sun/moon toggle, settings page).
  window.dispatchEvent(new CustomEvent<Theme>('rex-theme', { detail: theme }));
}

/** Persist + apply. Use when the user clicks the toggle. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
  applyTheme(theme);
}

/** Forget the user's choice and revert to OS preference. */
export function clearTheme(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  applyTheme(osPrefersDark() ? 'dark' : 'light');
}

/**
 * Boot — call once at app startup before the first render. Reads the
 * stored choice (or OS fallback) and applies it, and also wires a
 * listener so OS changes propagate if the user hasn't made an explicit
 * choice.
 */
export function initTheme(): void {
  applyTheme(effectiveTheme());

  // If the user hasn't made an explicit choice, follow OS changes.
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener?.('change', () => {
      if (readStoredTheme() === null) {
        applyTheme(osPrefersDark() ? 'dark' : 'light');
      }
    });
  }
}

/** Subscribe to theme changes. Returns an unsubscribe function. */
export function onThemeChange(fn: (t: Theme) => void): () => void {
  const handler = (e: Event) => {
    fn((e as CustomEvent<Theme>).detail);
  };
  window.addEventListener('rex-theme', handler);
  return () => window.removeEventListener('rex-theme', handler);
}
